import * as React from 'react';
import ContractFilter from './ContractFilter';
import TreeIdFilter from "./TreeIdFilter";
import ValidatorFilter from "./ValidatorFilter";
import DateRangeFilter from './DateFilter';
import TreeMaps from "../TreeMaps";
import * as UserRepository from "../../../Repository/UserRepository";
import { Collapse } from "antd/es";
import { ConnectionState, GlobalContext } from "../../../Contexts/GlobalContext";
import { Row, Col, Form, Button, message, Space, Checkbox } from 'antd';
import { isMobile } from 'react-device-detect';

const { Panel } = Collapse;

interface FiltersProps {
  filter: any;
  trees: any;
  mapRef: any;
  getTrees: Function;
  updateState: Function;
  loading?: boolean;
}

export interface MapPageFormFilter {
  isLoading: boolean;
  onChange?: Function;
  value?: any;
}

export default function Filters(props: FiltersProps) {
  const { mapRef, loading } = props;
  const { onlineStatus, user } = React.useContext(GlobalContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [activeKey, setActiveKey] = React.useState<string | string[]>([]);
  const [form] = Form.useForm();

  const onCollapseChange = (key: string | string[]) => {
    setActiveKey(key);
  };

  const onReset = () => {
    const zoomOut = form.getFieldValue('zoomOut');
    form.resetFields();
    form.setFieldsValue({ zoomOut });
    onFinish({
      treeId: null,
      contractId: null,
      validatorId: null,
      dateStart: null,
      dateEnd: null,
      zoomOut,
    });
    if (isMobile) {
      setActiveKey([]);
    }
  }

  const onFinish = async (values: any) => {
    try {
      const mapObj = (mapRef as React.MutableRefObject<TreeMaps>).current;

      if (isMobile) {
        // First close the filters menu to ensure smooth UI interaction
        setActiveKey([]);
        // Small delay to allow the drawer/collapse animation to begin
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setIsLoading(true);
      if (onlineStatus === ConnectionState.offline) {
        message.info('No data connection available');
        setIsLoading(false);
        return;
      }

      // We use a small timeout to ensure the UI has updated (closing the menu) 
      // before we trigger the intensive map filtering operations
      setTimeout(async () => {
        try {
          // Transform dates array to flat dateStart/dateEnd (convert to Unix timestamps in seconds)
          const filterValues = {
            ...values,
            ...(values.dates && Array.isArray(values.dates) && {
              dateStart: values.dates[0] ? Math.floor(values.dates[0].valueOf() / 1000) : null,
              dateEnd: values.dates[1] ? Math.floor(values.dates[1].valueOf() / 1000) : null,
            }),
          };
          // Remove dates array since we now use flat properties
          delete filterValues.dates;

          // 1. Update trees list & counter (left-side) first
          await props.getTrees(undefined, filterValues, { currentPage: 1 });
          // 2. Update trees on the map (right-side) second
          await mapObj?.applyFilter(filterValues);
          setIsLoading(false);
        } catch (err) {
          console.error(err);
          message.error(err.message);
          setIsLoading(false);
        }
      }, isMobile ? 300 : 0);

    } catch (err) {
      console.error(err);
      message.error(err.message);
      setIsLoading(false);
    }
  };

  return (
    <Collapse
      ghost
      style={{ paddingBottom: '10px' }}
      activeKey={activeKey}
      onChange={onCollapseChange}
    >
      <Panel header="Filters" key="1">
        <Form
          form={form}
          onFinish={onFinish}
          name="mapFilters"
          className="ant-advanced-search-form"
          initialValues={{
            treeId: null,
            contractId: null,
            validatorId: null,
            dates: [null, null],
            zoomOut: true,
          }}
        >
          <div className="filter-form-container">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Form.Item
                  name={`treeId`}
                  label={`Tree ID`}
                  className="filter-form-item"
                  rules={[
                    {
                      validator: (_: any, value: any) => {
                        if (value && isNaN(parseInt(value))) {
                          return Promise.reject();
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <TreeIdFilter isLoading={isLoading} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name={`contractId`}
                  label={`Contract`}
                  className="filter-form-item"
                  rules={[{ required: false }]}
                >
                  <ContractFilter isLoading={isLoading} />
                </Form.Item>
              </Col>
              <Col xs={24} md={7}>
                <Form.Item
                  name={`dates`}
                  label={`Dates`}
                  className="filter-form-item date-filter"
                  rules={[{ required: false }]}
                >
                  <DateRangeFilter isLoading={isLoading} />
                </Form.Item>
              </Col>
              {onlineStatus === ConnectionState.online && user && UserRepository.userHasRole(user.roles, UserRepository.UserRoles.Admin.name) && (
                <Col xs={24} md={5}>
                  <Form.Item
                    name={`validatorId`}
                    label={`Validator`}
                    className="filter-form-item"
                    rules={[{ required: false }]}
                  >
                    <ValidatorFilter isLoading={isLoading} />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <div className="filter-actions">
              <Form.Item
                name={`zoomOut`}
                valuePropName={"checked"}
                className="zoom-out-checkbox"
              >
                <Checkbox indeterminate={false}>
                  Zoom out on filter
                </Checkbox>
              </Form.Item>
              <Space size="middle">
                <Form.Item className="form-action-item">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    disabled={loading}
                    className="filter-button"
                  >
                    Filter
                  </Button>
                </Form.Item>
                <Form.Item className="form-action-item">
                  <Button
                    htmlType="button"
                    onClick={onReset}
                    disabled={isLoading || loading}
                    className="reset-button"
                  >
                    Reset
                  </Button>
                </Form.Item>
              </Space>
            </div>
          </div>
        </Form>
      </Panel>
    </Collapse>
  );
}